'use client'

import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { cn } from '@/core/utils/common'

const SCREEN_BACKGROUND = '#0e0d12'

/* 문서 안 기본값 — 앱과 같은 어두운 바탕·글꼴. 작가 CSS 가 뒤에 오므로 얼마든지 덮는다.
   CSP: 스크립트·외부 요청 전부 금지. 이미지·글꼴은 우리 도메인('self' — srcdoc 문서는 앱과 같은 출처)과 data: 만.
   외부 주소를 열어 두면 작가 틀의 <img src="https://…?{{속마음}}"> 같은 한 줄로 이용자 대화가 밖으로 새어 나간다 */
const HEAD = `<meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src 'self' data:; font-src 'self' data:; style-src 'unsafe-inline'">
<style>html,body{color-scheme:dark;margin:0;padding:0;background:${SCREEN_BACKGROUND};color:#f5f5f7;font:14px/1.6 Pretendard,system-ui,sans-serif;word-break:keep-all;overflow-wrap:anywhere}
html,body{overflow:hidden}body{display:flow-root;padding:3px}*,*:before,*:after{box-sizing:border-box}img{max-width:100%}</style>`
const DOCUMENT = `<!doctype html><html><head>${HEAD}<meta name="veluvit-screen" content="1"></head><body></body></html>`
/* 스크롤 금지(2026-10-07 "x, y 스크롤이 생기면 안 되지") — 높이는 바깥이 내용에 맞춰 늘리므로 안에서 스크롤할 일이 없다.
   body 여백 3px — 작가 틀의 모서리 장식(시스템창 꺾쇠 -2px 등)이 문서 밖으로 튀어나가 가로 스크롤을 만들던 것을 품는다.
   flow-root — 맨 위 · 맨 아래 요소의 바깥 여백이 body 밖으로 새어 높이 재기에서 빠지지 않게 */

/* 샌드박스·CSP 로도 막히지 않는 길 — iframe 이 스스로 다른 주소로 가는 것(<meta refresh>, 링크 누르기)과 외부 문서 끌어오기.
   값이 주소에 실려 나갈 수 있으므로 이런 태그와 주소 속성은 그리기 전에 걷어 낸다. 서버도 저장할 때 같은 규칙으로 거른다(요청서) */
const BLOCKED_TAGS =
  /<\/?(?:script|meta|link|base|iframe|frame|frameset|object|embed|form|portal|noscript)\b[^>]*>/gi
const BLOCKED_ATTRS =
  /\s(?:href|action|formaction|xlink:href|srcset|ping|on[a-z]+)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi

function stripUnsafe(html: string) {
  return html.replace(BLOCKED_TAGS, '').replace(BLOCKED_ATTRS, '')
}

/**
 * 작가 HTML 화면 — 스크립트 없는 샌드박스 iframe 에 그린다.
 * sandbox="allow-same-origin" 만 준다: 안에서 스크립트·폼·팝업·이동은 전부 막히고, 바깥(이 컴포넌트)만 문서 높이를 재서 iframe 높이를 맞춘다.
 * allow-scripts 와 allow-same-origin 을 함께 주면 샌드박스가 무력해지므로 스크립트는 끝까지 허용하지 않는다.
 */
export default function CustomScreen({
  html,
  title,
  className,
}: {
  html: string
  title: string
  className?: string
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(120)
  const safeHtml = useMemo(() => stripUnsafe(html), [html])

  useLayoutEffect(() => {
    const el = frame.current
    if (!el) return
    let observer: ResizeObserver | null = null
    const measure = () => {
      const doc = el.contentDocument
      // about:blank에는 아직 CSP가 없다. 제한된 srcdoc 문서가 준비된 뒤에만 본문을 넣는다.
      if (!doc?.body || !doc.head.querySelector('meta[name="veluvit-screen"]'))
        return
      // 값이 바뀌어도 iframe 문서를 다시 탐색하지 않아 흰 화면과 문서 재로딩을 막는다.
      doc.body.innerHTML = safeHtml
      // 내용 높이는 body 로 잰다 — html 은 iframe 창 크기와 같아 내용이 늘어도 크기가 바뀌지 않아 감시가 놓쳤다
      const fit = () => {
        const next = Math.max(
          1,
          Math.ceil(doc.body.getBoundingClientRect().height)
        )
        setHeight((previous) => (previous === next ? previous : next))
      }
      fit()
      observer?.disconnect()
      observer = new ResizeObserver(fit)
      observer.observe(doc.body)
    }
    el.addEventListener('load', measure)
    measure()
    return () => {
      el.removeEventListener('load', measure)
      observer?.disconnect()
    }
  }, [safeHtml])

  // 여백 · 둥글기 같은 바깥 모양(className)은 감싸는 상자에 — iframe 에 여백을 주면(border-box) 그만큼 안쪽이 작아져 세로 스크롤이 생겼다
  return (
    <div className={cn('min-w-0', className)}>
      <iframe
        ref={frame}
        title={title}
        sandbox="allow-same-origin"
        referrerPolicy="no-referrer"
        scrolling="no"
        srcDoc={DOCUMENT}
        style={{
          height,
          colorScheme: 'dark',
          backgroundColor: SCREEN_BACKGROUND,
        }}
        className="block w-full overflow-hidden border-0"
      />
    </div>
  )
}
